/**
 * API Client - HTTP client for the control-plane API
 */

import type {
  MagicLinkRequest,
  MagicLinkVerifyRequest,
  TokenRefreshRequest,
  MagicLinkSentResponse,
  PlatformAdminAuthTokenResponse,
  PlatformAdminAuthUser,
  ApiError as ApiErrorResponse,
} from '@resolve-ai/shared';

import { API_BASE } from './constants';
import { getAccessToken } from './storage';

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public statusCode: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Generic fetch wrapper with authentication.
 * Single source of truth for fetchWithAuth - import this rather than duplicating.
 */
export async function fetchWithAuth<T>(
  url: string,
  // eslint-disable-next-line no-undef
  options: RequestInit = {}
): Promise<T> {
  const token = getAccessToken();
  const headers = new Headers(options.headers);

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (options.body) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = (await response.json()) as ApiErrorResponse;
    throw new ApiError(error.code, error.message, response.status);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

/**
 * Platform-admin auth API client
 */
export const authApi = {
  async sendMagicLink(email: string): Promise<MagicLinkSentResponse> {
    const body: MagicLinkRequest = { email };
    return fetchWithAuth<MagicLinkSentResponse>('/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async verifyMagicLink(token: string): Promise<PlatformAdminAuthTokenResponse> {
    const body: MagicLinkVerifyRequest = { token };
    return fetchWithAuth<PlatformAdminAuthTokenResponse>('/auth/magic-link/verify', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async refreshTokens(refreshToken: string): Promise<PlatformAdminAuthTokenResponse> {
    const body: TokenRefreshRequest = { refreshToken };
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = (await response.json()) as ApiErrorResponse;
      throw new ApiError(error.code, error.message, response.status);
    }

    return response.json() as Promise<PlatformAdminAuthTokenResponse>;
  },

  async logout(refreshToken: string): Promise<void> {
    await fetchWithAuth<{ message: string }>('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });
  },

  async getCurrentAdmin(): Promise<PlatformAdminAuthUser> {
    return fetchWithAuth<PlatformAdminAuthUser>('/auth/me');
  },

  /**
   * Exchange a one-time OAuth transfer code (from the Google callback redirect)
   * for real tokens.
   */
  async exchangeCode(code: string): Promise<PlatformAdminAuthTokenResponse> {
    const response = await fetch(`${API_BASE}/auth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      const error = (await response.json()) as ApiErrorResponse;
      throw new ApiError(error.code, error.message, response.status);
    }

    return response.json() as Promise<PlatformAdminAuthTokenResponse>;
  },
};
